import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/** One thing the menu offers. */
export interface CellMenuItem {
  label: string;
  onSelect: () => void;
}

/** Its own rather than the kit's, which never opens under test; portalled, since the grids clip. */
const CellMenu = ({
  at,
  items,
  onClose,
}: {
  at: { x: number; y: number } | null;
  items: CellMenuItem[];
  onClose: () => void;
}): JSX.Element | null => {
  const menu = useRef<HTMLDivElement>(null);
  const first = useRef<HTMLButtonElement>(null);

  // The first item takes focus: the copy key opens this too, and only the mouse answering is a dead end.
  useEffect(() => {
    if (at) first.current?.focus();
  }, [at]);

  useEffect(() => {
    if (!at) return undefined;
    // A press inside is somebody choosing: shutting on it took the item out from under the pointer.
    const shut = (event?: Event): void => {
      if (event && menu.current?.contains(event.target as Node)) return;
      onClose();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    // Captured, so a press anywhere shuts it first; scrolling too, since it is placed against the page.
    window.addEventListener('pointerdown', shut, true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', shut, true);
    return () => {
      window.removeEventListener('pointerdown', shut, true);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', shut, true);
    };
  }, [at, onClose]);

  if (!at || !items.length) return null;

  return createPortal(
    <div
      ref={menu}
      role="menu"
      // Portalled to the body, so this is the only way to tell it belongs to the grid.
      data-cell-menu=""
      // Held inside the viewport, or a right-click near an edge opens a menu half off the screen.
      style={{
        position: 'fixed',
        top: Math.min(at.y, window.innerHeight - items.length * 36 - 16),
        left: Math.min(at.x, window.innerWidth - 200),
      }}
      className="z-50 min-w-44 rounded-lg border border-border bg-popover p-1 shadow-md"
    >
      {items.map((item, index) => (
        <button
          key={item.label}
          type="button"
          role="menuitem"
          onClick={() => item.onSelect()}
          ref={index === 0 ? first : undefined}
          className="w-full cursor-pointer rounded-md px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none"
        >
          {item.label}
        </button>
      ))}
    </div>,
    document.body,
  );
};

export default CellMenu;
